import React, { useState, useEffect, useCallback } from "react";
import {
  FaGithub,
  FaExternalLinkAlt,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaYoutube,
} from "react-icons/fa";
import "./Work.css";

const projects = [
  {
    title: "Sentient-Trader",
    shortDescription:
      "AI-powered real-time trading intelligence platform that transforms financial news into actionable trading signals using FinBERT sentiment analysis and 27 technical indicators.",
    image: "/Project/Sentinent_Trader/Sentinent Trader.png",
    techStack: [
      "Python",
      "FastAPI",
      "Kafka",
      "Redis",
      "Docker",
      "FinBERT",
      "Kubernetes",
    ],
    githubLink: "https://github.com/vikasp07/Sentient-Trader",
    badge: null,
    fullDescription: `Sentient-Trader is a production-ready, real-time trading intelligence platform that combines cutting-edge NLP with market data processing to generate actionable trading signals. The system leverages FinBERT for financial sentiment analysis, computes 27 technical indicators in real-time, uses Kafka (Redpanda) for distributed streaming, Redis for low-latency feature retrieval, and exposes a FastAPI-based REST API.`,
    features: [
      "Real-time multi-source news aggregation from Google News & NewsAPI",
      "NLP-based stock ticker extraction with confidence scoring",
      "FinBERT-powered financial sentiment classification (positive/negative/neutral)",
      "27 real-time technical indicators: RSI, MACD, Bollinger Bands, SMA, EMA, ATR, OBV, VWAP, and more",
      "Multi-factor trading signal generation combining sentiment + technical analysis",
      "Historical backtesting engine with PnL tracking and trade ledger export",
      "12-service Docker Compose deployment with Kubernetes-ready manifests",
      "Three trading modes: News-Only, Partial, and Full multi-factor analysis",
    ],
    gallery: ["/Project/Sentinent_Trader/Sentinent Trader.png"],
  },
  {
    title: "Game Therapy",
    shortDescription:
      "An adaptive gaming platform to support individuals with cognitive, social, and emotional challenges through interactive games, AI therapy chatbot, and gesture-based controls.",
    image: "/Project/Game_Theraphy/GameTheraphy.jpg",
    techStack: [
      "MERN Stack",
      "Twilio",
      "Google APIs",
      "TensorFlow",
      "Hugging Face",
      "JWT",
      "Charts.js",
    ],
    githubLink: "https://github.com/vikasp07/Game-Theraphy",
    liveLink: "https://game-theraphy.onrender.com/",
    liveLinkLabel: "Live Site",
    videoLink: "https://www.youtube.com/watch?v=Lg2mKiUPsIM",
    badge: null,
    fullDescription: `Game Therapy is an adaptive gaming platform designed to support individuals with cognitive, social, and emotional challenges. It combines innovative games, real-time AI support, and gesture-based controls to create a personalized therapeutic experience. The platform includes a dyslexia-friendly Family Tree game using Arduino-based hand gestures, an interactive Astra Maze for motor skills, an AI therapy chatbot powered by Llama and other LLMs, a guardian dashboard with progress tracking, and a community support chat.`,
    features: [
      "Family Tree Game: dyslexia-friendly game using Arduino-based hand gestures for name recognition",
      "Astra Maze: interactive ball maze controlled via motion tracking for motor skill enhancement",
      "AI Therapy Chatbot powered by Llama and other LLMs for personalized emotional support",
      "Guardian Dashboard with interactive charts and analytics for progress monitoring",
      "Community Support platform for safe peer interaction",
      "Secure authentication system with JWT tokens",
      "Real-time communication via Twilio and Google APIs",
    ],
    gallery: [
      "/Project/Game_Theraphy/GameTheraphy.jpg",
      "/Project/Game_Theraphy/gameTheraphy-other.jpg",
      "/Project/Game_Theraphy/guard.jpg",
    ],
  },
  {
    title: "Habitat",
    shortDescription:
      "A GIS-powered intelligent reforestation planning and monitoring platform that combines real-time satellite imagery, weather data, soil analysis, and AI-powered recommendations.",
    image: "/Project/Habitat/Habitat.png",
    techStack: [
      "Next.js 16",
      "React 19",
      "TypeScript",
      "Leaflet",
      "Tailwind CSS",
      "OpenAI/Gemini",
    ],
    githubLink: "https://github.com/vikasp07/hack-i",
    liveLink: "https://habitat-tsechacks.vercel.app/",
    liveLinkLabel: "Live Site",
    videoLink: "https://www.youtube.com/watch?v=PVFCDZHA3nM",
    badge: null,
    fullDescription: `Habitat is an intelligent, GIS-powered adaptive reforestation platform that helps environmental planners, researchers, and organizations make data-driven decisions for ecosystem restoration in India. The platform combines real-time satellite imagery, weather data, soil analysis, and AI-powered recommendations to provide comprehensive reforestation planning, monitoring, and prediction capabilities.`,
    features: [
      "Interactive Leaflet map with India-centric focus and forest zone overlays (Western/Eastern Ghats, Central Highlands)",
      "Real-time ecosystem health scoring (0-100) using weighted formula across 6 key metrics: NDVI, Moisture, Temperature, AQI, Forest Cover, Soil pH",
      "12-month historical data visualization with Recharts for trend analysis",
      "AI-powered risk advisory with drought/flood/heat wave predictions and species recommendations with suitability scores",
      "Calamity simulation engine — model impact of drought, flood, heat wave, frost, or pest outbreak with survival rates and recovery estimates",
      "AI Chat Assistant powered by GPT-4/Gemini with tool-calling capabilities for real-time data queries",
      "3-12 month NDVI forecasts and 5-20 year carbon sequestration estimates",
    ],
    gallery: [
      "/Project/Habitat/Habitat.png",
      "/Project/Habitat/Habitat_other.png",
    ],
  },
  {
    title: "CampusRick",
    shortDescription:
      "A ride-sharing application to simplify commuting and reduce waiting times for college students with real-time booking, triple-sharing, and live vehicle tracking.",
    image: "/Project/CampusRick/CampusRick.png",
    techStack: [
      "Node.js",
      "Flutter",
      "FlutterFlow",
      "SupaBase",
      "Google Maps API",
    ],
    githubLink: "https://github.com/vikasp07/CampusRick",
    liveLink: "https://campusrick.flutterflow.app/",
    liveLinkLabel: "Live Site",
    badge: null,
    fullDescription: `CampusRick is a ride-sharing application built to simplify commuting and reduce waiting times for college students. It features a real-time booking system with triple-sharing functionality, automated fare calculation, and live vehicle tracking using the Google Maps API. Built with Flutter and FlutterFlow for the mobile frontend and Node.js with SupaBase for the backend.`,
    features: [
      "Real-time ride booking system with instant matching",
      "Triple-sharing functionality for cost-effective commuting",
      "Automated fare calculation based on distance and sharing",
      "Live vehicle tracking using Google Maps API",
      "Cross-platform mobile app built with Flutter and FlutterFlow",
      "SupaBase backend for real-time data sync and authentication",
    ],
    gallery: [
      "/Project/CampusRick/CampusRick.png",
      "/Project/CampusRick/CampusRick-2.png",
      "/Project/CampusRick/CampusRick-3.png",
      "/Project/CampusRick/CampusRick4.png",
      "/Project/CampusRick/CampusRick-other.png",
    ],
  },
  {
    title: "WanderLust",
    shortDescription:
      "A full-stack travel platform enabling users to explore and share destinations with secure authentication, image uploads, and interactive listing management.",
    image: "/Project/Wanderlust/Screenshot - wanderlust.png",
    techStack: [
      "MongoDB",
      "Express.js",
      "Node.js",
      "Cloudinary",
      "Passport.js",
      "Bootstrap",
    ],
    githubLink: "https://github.com/vikasp07/Wander_lust",
    liveLink: "https://wander-lust-6cpq.onrender.com",
    liveLinkLabel: "Live Site",
    badge: null,
    fullDescription: `WanderLust is a full-stack travel platform that allows users to explore and share their favorite destinations. Built using the MERN stack, it incorporates Multer for image uploads, Cloudinary for image storage, and Passport.js for secure authentication. Users can create, edit, and delete travel listings, leave reviews, and search/filter destinations.`,
    features: [
      "Create, edit, and delete travel listings with rich details",
      "Leave reviews and ratings for destinations",
      "Search and filter listings based on preferences",
      "Secure user authentication with Passport.js (sign-up, login, logout)",
      "Image uploads via Multer with Cloudinary storage",
      "Responsive design with Bootstrap",
      "Deployed live on Render",
    ],
    gallery: ["/Project/Wanderlust/Screenshot - wanderlust.png"],
  },
];

const ProjectModal = ({ project, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const gallery = project.gallery || [];

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && gallery.length > 1)
        setCurrentImageIndex((prev) => (prev + 1) % gallery.length);
      if (e.key === "ArrowLeft" && gallery.length > 1)
        setCurrentImageIndex(
          (prev) => (prev - 1 + gallery.length) % gallery.length
        );
    },
    [onClose, gallery.length]
  );

  useEffect(() => {
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>

        {/* Gallery */}
        {gallery.length > 0 && (
          <div className="modal-gallery">
            <img
              src={gallery[currentImageIndex]}
              alt={`${project.title} screenshot ${currentImageIndex + 1}`}
              className="modal-gallery-image"
            />
            {gallery.length > 1 && (
              <>
                <button
                  className="gallery-nav gallery-prev"
                  onClick={() =>
                    setCurrentImageIndex(
                      (prev) => (prev - 1 + gallery.length) % gallery.length
                    )
                  }
                  aria-label="Previous image"
                >
                  <FaChevronLeft />
                </button>
                <button
                  className="gallery-nav gallery-next"
                  onClick={() =>
                    setCurrentImageIndex((prev) => (prev + 1) % gallery.length)
                  }
                  aria-label="Next image"
                >
                  <FaChevronRight />
                </button>
                <div className="gallery-dots">
                  {gallery.map((_, i) => (
                    <span
                      key={i}
                      className={`gallery-dot ${
                        i === currentImageIndex ? "active" : ""
                      }`}
                      onClick={() => setCurrentImageIndex(i)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">{project.title}</h2>
          {project.badge && (
            <span className="modal-badge">{project.badge}</span>
          )}
        </div>

        {/* Links */}
        <div className="modal-links">
          <a
            href={project.githubLink}
            target="_blank"
            rel="noopener noreferrer"
            className="modal-link"
          >
            <FaGithub /> GitHub
          </a>
          {project.liveLink && (
            <a
              href={project.liveLink}
              target="_blank"
              rel="noopener noreferrer"
              className="modal-link"
            >
              <FaExternalLinkAlt /> {project.liveLinkLabel || "Live Demo"}
            </a>
          )}
          {project.videoLink && (
            <a
              href={project.videoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="modal-link modal-link-video"
            >
              <FaYoutube /> Watch Demo
            </a>
          )}
        </div>

        {/* Description */}
        <div className="modal-body">
          <p className="modal-description">{project.fullDescription}</p>

          <h3 className="modal-subtitle">Key Features</h3>
          <ul className="modal-features">
            {project.features.map((feature, i) => (
              <li key={i}>{feature}</li>
            ))}
          </ul>

          <h3 className="modal-subtitle">Tech Stack</h3>
          <div className="modal-tech-stack">
            {project.techStack.map((tech, i) => (
              <span key={i} className="modal-tech-pill">
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Work = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  return (
    <section className="work-section" id="work">
      <h2 className="work-title">Some Things I&apos;ve Built</h2>
      <div className="work-grid">
        {projects.map((project, idx) => (
          <div className="project-card" key={idx}>
            {/* Card Image */}
            <div className="card-image-wrapper">
              {project.image ? (
                <img
                  src={project.image}
                  alt={project.title}
                  className="card-image"
                  loading="lazy"
                />
              ) : (
                <div className="card-image-placeholder">
                  <span className="placeholder-icon">&#60;/&#62;</span>
                </div>
              )}
              {project.badge && (
                <span className="card-badge">{project.badge}</span>
              )}
            </div>

            {/* Card Body */}
            <div className="card-body">
              <div className="card-header">
                <h3 className="project-title">{project.title}</h3>
                <div className="card-links">
                  <a
                    href={project.githubLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="github-link"
                    aria-label="GitHub"
                  >
                    <FaGithub />
                  </a>
                  {project.liveLink && (
                    <a
                      href={project.liveLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="live-link"
                      aria-label="Live Demo"
                    >
                      <FaExternalLinkAlt />
                    </a>
                  )}
                  {project.videoLink && (
                    <a
                      href={project.videoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="live-link video-link"
                      aria-label="Watch Demo"
                    >
                      <FaYoutube />
                    </a>
                  )}
                </div>
              </div>

              <p className="project-description">{project.shortDescription}</p>

              {/* Tech Stack */}
              <ul className="tech-stack">
                {project.techStack.map((tech, i) => (
                  <li key={i}>{tech}</li>
                ))}
              </ul>

              {/* Read More */}
              <button
                className="read-more-btn"
                onClick={() => setSelectedProject(project)}
              >
                Read More &rarr;
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {selectedProject && (
        <ProjectModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </section>
  );
};

export default Work;
